/**
 * Trees, shrubs and shoreline boulders (GDD §3.5; concept L314–L341, L726–L762). Placement consumes the world
 * generation stream (seed 41) in exactly the concept's order, so every tree lands where the concept puts it.
 */
import {
  BufferGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  IcosahedronGeometry,
  MeshStandardMaterial,
} from 'three';
import { sstep } from '../core/math.ts';
import type { Rng } from '../core/rng.ts';
import { instanced, jitter, merge, scatter, type Accept, type InstanceItem, type Part } from './geometry.ts';
import { pn } from './noise.ts';
import type { WorldProfile } from './profiles.ts';

const TAU = Math.PI * 2;
const col = (hex: number) => new Color(hex);

/** Pine with a four-tier crown (GDD §3.5; concept L315). Unit height; scaled per instance. */
export function pineGeo(): BufferGeometry {
  const parts: Part[] = [];
  const t = new CylinderGeometry(0.03, 0.05, 0.32, 6);
  t.translate(0, 0.16, 0);
  parts.push({ geo: t, color: col(0x4a3728) });
  (
    [
      [0.16, 0.44, 0.33, 0x2b5028],
      [0.38, 0.38, 0.26, 0x325c2d],
      [0.58, 0.32, 0.19, 0x396632],
      [0.77, 0.25, 0.11, 0x416f39],
    ] as const
  ).forEach((q, i) => {
    const g = new ConeGeometry(q[2], q[1], 9, 2);
    g.translate(0, q[0] + q[1] / 2, 0);
    jitter(g, 0.05, i * 3.1);
    parts.push({ geo: g, color: col(q[3]) });
  });
  return merge(parts);
}

/** Broadleaf tree with a clustered crown (GDD §3.5; concept L324). */
export function broadGeo(): BufferGeometry {
  const parts: Part[] = [];
  const t = new CylinderGeometry(0.035, 0.06, 0.5, 6);
  t.translate(0, 0.25, 0);
  parts.push({ geo: t, color: col(0x4d3b2b) });
  (
    [
      [0, 0.72, 0, 0.36, 0x4a7934],
      [0.2, 0.62, 0.12, 0.28, 0x578138],
      [-0.18, 0.66, -0.1, 0.27, 0x3f6b2d],
      [0.04, 0.92, -0.05, 0.25, 0x628c3e],
    ] as const
  ).forEach((q, i) => {
    const g = new IcosahedronGeometry(q[3], 1);
    jitter(g, 0.07, i * 7.7);
    g.translate(q[0], q[1], q[2]);
    parts.push({ geo: g, color: col(q[4]) });
  });
  return merge(parts);
}

/** Shrub: two jittered blobs (concept L333). */
export function shrubGeo(c1: number, c2: number): BufferGeometry {
  const a = new IcosahedronGeometry(0.5, 1);
  a.scale(1, 0.62, 1);
  jitter(a, 0.12, 2.2);
  a.translate(0, 0.26, 0);
  const b = new IcosahedronGeometry(0.34, 1);
  b.scale(1, 0.7, 1);
  jitter(b, 0.1, 4.4);
  b.translate(0.32, 0.2, 0.18);
  return merge([
    { geo: a, color: col(c1) },
    { geo: b, color: col(c2) },
  ]);
}

/** Boulder (concept L338). */
export function rockGeo(c: number): BufferGeometry {
  const g = new IcosahedronGeometry(1, 1);
  jitter(g, 0.5, 1.7);
  g.scale(1, 0.6, 1.15);
  g.translate(0, 0.15, 0);
  return merge([{ geo: g, color: col(c) }]);
}

export interface VegetationLayer {
  name: 'pine' | 'broad' | 'shrub' | 'rock';
  geometry: BufferGeometry;
  items: InstanceItem[];
}

/**
 * All vegetation layers for a world, in the concept's creation order (which is also its random-draw order).
 */
export function vegetationLayers(
  W: WorldProfile,
  rand: Rng,
  canyon: boolean,
  z0: number,
  z1: number,
): VegetationLayer[] {
  const hAt = (x: number, z: number) => W.height(x, z);
  const flatOK = (x: number, z: number, h: number, lim: number) =>
    Math.abs(hAt(x + 1.5, z) - h) + Math.abs(hAt(x, z + 1.5) - h) < lim;
  const layers: VegetationLayer[] = [];
  if (!canyon) {
    const treeAccept =
      (lo: number): Accept =>
      (x, z, r) => {
        const h = hAt(x, z);
        if (h < 1.4 || h > 62 || !flatOK(x, z, h, 1.7)) return null;
        const d = Math.abs(x - W.center(z)) - W.width(z);
        const dens = (d < 0 ? 0.9 : sstep(3, 9, d)) * (0.18 + 0.82 * sstep(-0.35, 0.45, pn(x * 1.9 + 13, z)));
        if (r() > dens) return null;
        const H = lo + r() * 7;
        const t = 0.82 + r() * 0.3;
        // Property order matters: each r() is drawn in the concept's order.
        const rot = r() * TAU;
        const lean = (r() - 0.5) * 0.08;
        const rr = t * (0.9 + r() * 0.14);
        const bb = t * (0.88 + r() * 0.14);
        return { x, y: h - 0.4, z, sx: H * 0.8, sy: H, sz: H * 0.8, rot, lean, r: rr, g: t, b: bb };
      };
    layers.push({ name: 'pine', geometry: pineGeo(), items: scatter(W, rand, 1500, 230, treeAccept(8), z0, z1) });
    layers.push({ name: 'broad', geometry: broadGeo(), items: scatter(W, rand, 1000, 230, treeAccept(6.5), z0, z1) });
    layers.push({
      name: 'shrub',
      geometry: shrubGeo(0x4f7a33, 0x5d8a3a),
      items: scatter(
        W,
        rand,
        700,
        120,
        (x, z, r) => {
          const h = hAt(x, z);
          if (h < 0.9 || h > 20) return null;
          const s = 1.4 + r() * 2.2;
          return { x, y: h - 0.15, z, sx: s, sy: s, sz: s, rot: r() * TAU, r: 1, g: 1, b: 1 };
        },
        z0,
        z1,
      ),
    });
  } else {
    layers.push({
      name: 'shrub',
      geometry: shrubGeo(0x6f6d3c, 0x7c7644),
      items: scatter(
        W,
        rand,
        900,
        200,
        (x, z, r) => {
          const h = hAt(x, z);
          if (h < 1.0 || !flatOK(x, z, h, 1.2)) return null;
          const s = 1.2 + r() * 2.4;
          return { x, y: h - 0.15, z, sx: s, sy: s * 0.8, sz: s, rot: r() * TAU, r: 1, g: 1, b: 1 };
        },
        z0,
        z1,
      ),
    });
    layers.push({
      name: 'pine',
      geometry: pineGeo(),
      items: scatter(
        W,
        rand,
        320,
        200,
        (x, z, r) => {
          const h = hAt(x, z);
          if (h < 30 || !flatOK(x, z, h, 1.0)) return null;
          const H = 4 + r() * 4;
          return { x, y: h - 0.2, z, sx: H * 0.9, sy: H, sz: H * 0.9, rot: r() * TAU, r: 0.85, g: 0.78, b: 0.6 };
        },
        z0,
        z1,
      ),
    });
  }
  layers.push({
    name: 'rock',
    geometry: rockGeo(canyon ? 0x8f4c30 : 0x8a857c),
    items: scatter(
      W,
      rand,
      canyon ? 520 : 300,
      60,
      (x, z, r) => {
        const c = W.center(z);
        const w = W.width(z);
        const side = x > c ? 1 : -1;
        const xx = c + side * (w + W.shore + (r() - 0.35) * (canyon ? 9 : 5));
        const h = hAt(xx, z);
        if (h < -0.8 || h > (canyon ? 14 : 4)) return null;
        const s = (canyon ? 0.8 : 0.7) + r() * (canyon ? 2.0 : 1.8);
        const t = 0.85 + r() * 0.3;
        const rot = r() * TAU;
        const lean = (r() - 0.5) * 0.4;
        return { x: xx, y: h - s * 0.2, z, sx: s, sy: s, sz: s, rot, lean, r: t, g: t, b: t };
      },
      z0,
      z1,
    ),
  });
  return layers;
}

/** Materials for vegetation and rocks (concept L726, L756). */
export function vegetationMaterials() {
  return {
    veg: new MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0, envMapIntensity: 0.5 }),
    rock: new MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0, envMapIntensity: 0.4 }),
  };
}

export function vegetationMeshes(layers: VegetationLayer[], mats: ReturnType<typeof vegetationMaterials>) {
  return layers.map((l) => instanced(l.geometry, l.name === 'rock' ? mats.rock : mats.veg, l.items, true));
}

/**
 * The same instances split into z-bands, each an InstancedMesh with its own bounds, so frustum culling skips
 * off-screen trees (GDD §10.3). Pixels are unchanged; the tests compare against the unbanded meshes.
 */
export function bandedVegetationMeshes(
  layers: VegetationLayer[],
  mats: ReturnType<typeof vegetationMaterials>,
  band: number,
) {
  const out = [];
  for (const l of layers) {
    const bands = new Map<number, InstanceItem[]>();
    for (const it of l.items) {
      const k = Math.floor(it.z / band);
      const list = bands.get(k);
      if (list) list.push(it);
      else bands.set(k, [it]);
    }
    for (const items of bands.values()) {
      const m = instanced(l.geometry, l.name === 'rock' ? mats.rock : mats.veg, items, true);
      m.frustumCulled = true;
      m.computeBoundingSphere();
      out.push(m);
    }
  }
  return out;
}
